import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import axios from 'axios';
import {
  AppInputNumber,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { axiosPath } from '@/routing/helpers/router';
import * as currentPageAction from '@/store/actions/currentPageAction';
import colors from '@/constants/colors';
import AppSpinner from '@/layout/components/AppSpinner';
import styled from 'styled-components';

export default function SurveyQuestion({ parentQuestion, question }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const surveyUser = useSelector((state) => state.currentPage?.surveyUser);
  const [isLoading, setIsLoading] = useState(false);

  const saveAnswer = useCallback(
    async (questionID, value) => {
      await axios.post(axiosPath('api_r_user_module_survey_edit'), {
        moduleBridgeHex,
        parentQuestionID: parentQuestion?.id,
        questionID,
        value,
      });

      setIsLoading(false);
    },
    [moduleBridgeHex, parentQuestion],
  );

  const debouncedSaveAnswer = useMemo(() => {
    return _.debounce((questionID, value) => {
      saveAnswer(questionID, value);
    }, 1000);
  }, [saveAnswer]);

  useEffect(() => {
    return () => {
      debouncedSaveAnswer.cancel();
    };
  }, [debouncedSaveAnswer]);

  const handleChange = useCallback(
    (questionID, value) => {
      setIsLoading(true);
      const updatedSurveyUser = { ...surveyUser };
      const updatedUserAnswers = {
        ...(updatedSurveyUser.userAnswers || []),
        [questionID]: value,
      };

      if (question?.type === 0) {
        const isExpand = question?.answers?.find((a) => a?.value == value)?.isExpand;
        const currentExpandedQuestions = updatedSurveyUser.expandQuestions || [];
        updatedSurveyUser.expandQuestions = isExpand
          ? [...currentExpandedQuestions, questionID]
          : currentExpandedQuestions.filter((id) => id !== questionID);

        if (!isExpand && question?.questions?.length > 0) {
          const questionIDs = question?.questions.map((q) => q.id);

          questionIDs.forEach((id) => {
            delete updatedUserAnswers[id];
          });
        }
      }

      updatedSurveyUser.userAnswers = updatedUserAnswers;
      dispatch(currentPageAction.change('surveyUser', updatedSurveyUser, true));
      debouncedSaveAnswer(questionID, value);
    },
    [surveyUser, question],
  );

  return (
    <React.Fragment
      key={parentQuestion ? `${parentQuestion?.id}-${question?.id}` : question?.id}
    >
      {question?.type === 3 ? (
        <h3 className="font-weight-bold my-4">{question?.description}</h3>
      ) : (
        <h6 className="font-weight-bold">{question?.description}</h6>
      )}
      <Container>
        <div className="mr-2">
          {isLoading && (
            <AppSpinner>
              <AppRemixIcon icon="loader-4" size={20} color={colors.primaryLight} />
            </AppSpinner>
          )}
        </div>
        {question?.type === 0 && (
          <AppSingleSelect
            options={question?.answers}
            value={surveyUser?.userAnswers?.[question?.id] || null}
            onChange={(v) => handleChange(question?.id, v)}
            isClearable
          />
        )}
        {question?.type === 1 && (
          <AppInputNumber
            value={surveyUser?.userAnswers?.[question?.id] || ''}
            onChange={(v) => handleChange(question?.id, v)}
          />
        )}
        {question?.type === 2 && (
          <AppTextArea
            value={surveyUser?.userAnswers?.[question?.id] || ''}
            onChange={(v) => handleChange(question?.id, v)}
            resize="vertical"
          />
        )}
      </Container>

      <div className="ml-5">
        {surveyUser?.expandQuestions?.includes(question?.id) &&
          question?.questions?.map((q) => (
            <SurveyQuestion parentQuestion={question} question={q} />
          ))}
      </div>
    </React.Fragment>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  & > :nth-child(1) {
    width: 20px;
  }
  & > :nth-child(2) {
    flex: 1;
  }
`;
